// Managing events (/events/admins) — admins only.
//
// Make one (title → slug, when, Discord link, the blurb people read), and
// every event below it: its status in one switch (Upcoming / Live / Ended),
// its page, its run view, and delete — asked twice, inline.

import { useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Plus, Trash2 } from "lucide-react";
import type { ApiEvent, ApiEventList, EventStatus } from "@shared/api-types.js";
import { Button } from "@web-shared/components/ui/button";
import { Skeleton } from "@web-shared/components/ui/skeleton";
import { json, webApi } from "@web-shared/lib/web-api";
import { cn } from "@web-shared/lib/utils";
import { eventPath } from "@/lib/ctas";
import { AmbientGlow } from "@/components/AmbientGlow";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { DateBlock } from "../components/EventParts";
import { useEventList } from "./EventsPage";

type ListedEvent = ApiEventList["events"][number];

const DEFAULT_BLURB = "Drop a loop in the pool, get someone else's seed, flip it live.\nPlay it back on the call.";
const DEFAULT_DISCORD = "https://seed-bed.com/join";

/** "Secret Sample #2!" → "secret-sample-2" */
function slugOf(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

export function EventsAdminPage() {
  useDocumentTitle("Manage events");
  const list = useEventList();

  if (list.isPending) {
    return (
      <div className="mx-auto max-w-3xl space-y-3">
        <AmbientGlow />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }
  if (list.isError || !list.data.isAdmin) {
    return (
      <div className="py-16 text-center">
        <AmbientGlow />
        <p className="text-lg font-bold">{list.isError ? "Couldn't load events." : "Admins only."}</p>
        <Button asChild className="mt-5 h-11 rounded-full px-6 font-bold">
          <Link to="/events">
            <ArrowLeft className="size-4" /> All events
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-8 pb-10">
      <AmbientGlow />
      <Link
        to="/events"
        className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-raised/60 px-3 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" /> All events
      </Link>
      <header>
        <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-primary">Admin</p>
        <h1 className="mt-1 text-4xl font-black tracking-tighter">Manage events</h1>
      </header>
      <CreateEvent />
      <section className="space-y-3">
        <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
          All events · {list.data.events.length}
        </h2>
        {list.data.events.length === 0 ? (
          <p className="text-sm text-muted-foreground">None yet. Make the first one above.</p>
        ) : (
          list.data.events.map((e) => <ManagedEvent key={e.slug} event={e} />)
        )}
      </section>
    </div>
  );
}

function Field({ label, hint, children, className }: { label: string; hint?: string; children: ReactNode; className?: string }) {
  return (
    <label className={cn("block space-y-1.5", className)}>
      <span className="text-xs font-semibold text-muted-foreground">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-muted-foreground/70">{hint}</span>}
    </label>
  );
}

/** Fields are text-base on a phone: iOS zooms into anything smaller it focuses. */
const INPUT =
  "w-full rounded-lg border border-white/10 bg-background/70 px-3 py-2 text-base outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-primary/60 sm:text-sm";

function CreateEvent() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [discordUrl, setDiscordUrl] = useState(DEFAULT_DISCORD);
  const [blurb, setBlurb] = useState(DEFAULT_BLURB);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const finalSlug = slugTouched ? slug : slugOf(title);
  const toIso = (local: string) => (local ? new Date(local).toISOString() : undefined);

  const create = () => {
    setBusy(true);
    setError(null);
    webApi<ApiEvent>(
      "/events",
      json("POST", { title, slug: finalSlug, blurb, startsAt: toIso(startsAt), endsAt: toIso(endsAt), discordUrl }),
    )
      .then(async (e) => {
        await queryClient.invalidateQueries({ queryKey: ["web", "events"] });
        navigate(`${eventPath(e.slug)}/admin`);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Couldn't create it."))
      .finally(() => setBusy(false));
  };

  return (
    <section
      className="space-y-4 rounded-2xl border border-primary/25 bg-raised p-5 sm:p-6"
      style={{ backgroundImage: "linear-gradient(120deg, color-mix(in srgb, var(--primary) 8%, transparent), transparent 55%)" }}
    >
      <h2 className="flex items-center gap-2 text-lg font-black">
        <Plus className="size-5 text-primary" /> New event
      </h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Title">
          <input className={INPUT} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Secret Sample #2" />
        </Field>
        <Field label="Link" hint={`${window.location.host}/events/${finalSlug || "…"}`}>
          <input
            className={INPUT}
            value={finalSlug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(slugOf(e.target.value));
            }}
            placeholder="secret-sample-2"
          />
        </Field>
        <Field label="Starts" hint="Your time zone">
          <input type="datetime-local" className={INPUT} value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
        </Field>
        <Field label="Ends (optional)">
          <input type="datetime-local" className={INPUT} value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
        </Field>
        <Field label="Discord link" className="sm:col-span-2">
          <input className={INPUT} value={discordUrl} onChange={(e) => setDiscordUrl(e.target.value)} />
        </Field>
        <Field label="What people read" hint="Shown under the title. A line break stays a line break." className="sm:col-span-2">
          <textarea className={cn(INPUT, "min-h-20 resize-y")} value={blurb} onChange={(e) => setBlurb(e.target.value)} />
        </Field>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={create} disabled={busy || !title.trim() || !finalSlug} className="rounded-full px-6 font-bold">
          {busy && <Loader2 className="size-4 animate-spin" />} Create event
        </Button>
        {error && <p className="text-sm text-removed">{error}</p>}
      </div>
    </section>
  );
}

const STATUS_OPTIONS: { value: EventStatus; label: string }[] = [
  { value: "open", label: "Upcoming" },
  { value: "assigned", label: "Live" },
  { value: "closed", label: "Ended" },
];

function ManagedEvent({ event }: { event: ListedEvent }) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = (init: RequestInit) => {
    setBusy(true);
    setError(null);
    webApi(`/events/${encodeURIComponent(event.slug)}`, init)
      .then(() => queryClient.invalidateQueries({ queryKey: ["web", "events"] }))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Something went wrong."))
      .finally(() => {
        setBusy(false);
        setConfirmDelete(false);
      });
  };

  return (
    <article className="flex flex-col gap-4 rounded-2xl border border-white/10 bg-raised/80 p-4 sm:flex-row sm:items-center">
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <DateBlock iso={event.startsAt} />
        <div className="min-w-0">
          <Link to={eventPath(event.slug)} className="block truncate text-lg font-black tracking-tight hover:underline">
            {event.title}
          </Link>
          <p className="truncate text-xs text-muted-foreground">
            /events/{event.slug} · {event.participantCount} in · {event.poolCount} pooled
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {/* Status as one switch. "Live" is what Assign sets; setting it here
            doesn't draw seeds — the run view's Assign does. */}
        <div className="flex rounded-full border border-white/10 bg-background/60 p-0.5" role="radiogroup" aria-label="Status">
          {STATUS_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={event.status === o.value}
              disabled={busy || event.status === o.value}
              onClick={() => send(json("PATCH", { status: o.value }))}
              className={cn(
                "rounded-full px-3 py-1 text-xs font-semibold transition-colors",
                event.status === o.value ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
        <Button asChild size="sm" className="rounded-full font-bold">
          <Link to={`${eventPath(event.slug)}/admin`}>Run</Link>
        </Button>
        {confirmDelete ? (
          <span className="flex items-center gap-1">
            <Button size="sm" variant="outline" disabled={busy} onClick={() => send(json("DELETE"))} className="rounded-full border-removed/50 text-removed">
              Delete it
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)} className="rounded-full">
              Keep
            </Button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            aria-label={`Delete ${event.title}`}
            className="flex size-8 items-center justify-center rounded-full text-muted-foreground hover:bg-white/5 hover:text-removed"
          >
            <Trash2 className="size-4" />
          </button>
        )}
      </div>
      {error && <p className="text-sm text-removed sm:basis-full">{error}</p>}
    </article>
  );
}
